import { DEFAULT_LOCALE, LOCALES, type SupportedLocale } from '../cms/cmsLocaleConstants';
import { getLanguageCodeFromLocale, getOptionalLocaleForCMS } from '../cms/localeValidationUtils';
import { getUserLocalePreference } from '../users/utils/userPropertyUtils';
import { AMPLITUDE_USER_PROPS } from './statsConstants';

type UserWithLocalePreference = {
    settings?: {
        localePreference?: string | null;
    };
};

const AMPLITUDE_ENGAGMENT_LOCALE_MAP: Record<SupportedLocale, string> = {
    [LOCALES.ENGLISH]: 'en-AU',
    [LOCALES.SPANISH]: 'es-ES',
    [LOCALES.FRENCH]: 'fr-FR',
    [LOCALES.GERMAN]: 'de-DE',
    [LOCALES.PORTUGUESE_BRAZILIAN]: 'pt-BR',
    [LOCALES.CHINESE_SIMPLIFIED]: 'zh-CN',
    [LOCALES.RUSSIAN]: 'ru',
    [LOCALES.POLISH]: 'pl',
    [LOCALES.JAPANESE]: 'ja',
    [LOCALES.DUTCH]: 'nl',
    [LOCALES.ITALIAN]: 'it',
    [LOCALES.KOREAN]: 'ko',
};

export const DEFAULT_AMPLITUDE_ENGAGEMENT_LANGUAGE = AMPLITUDE_ENGAGMENT_LOCALE_MAP[DEFAULT_LOCALE] || 'en';

/**
 * Returns Amplitude user properties for a user's saved locale preference.
 */
export const getLocalizationPreferenceAmplitudeUserProperties = (
    user?: UserWithLocalePreference,
): Partial<Record<string, string>> => {
    const localePreference = getUserLocalePreference(user);
    if (!localePreference) return {};

    return {
        [AMPLITUDE_USER_PROPS.LOCALISATION_PREFERENCE]: localePreference,
    };
};

/**
 * Maps Milanote locale values to the language format expected by Amplitude
 * Guides and Surveys.
 */
export const getAmplitudeEngagementLanguage = (localePreference?: string | null): string => {
    const normalizedLocale = getOptionalLocaleForCMS(localePreference);
    if (!normalizedLocale) return DEFAULT_AMPLITUDE_ENGAGEMENT_LANGUAGE;

    const mappedLocale = AMPLITUDE_ENGAGMENT_LOCALE_MAP[normalizedLocale];
    if (mappedLocale) return mappedLocale;

    const languageCode = getLanguageCodeFromLocale(normalizedLocale);
    return languageCode || DEFAULT_AMPLITUDE_ENGAGEMENT_LANGUAGE;
};
